import type { AuthoredRoleTracks } from './pair-clips.js';

/**
 * A one-character animation authored in Blender: the body's joint tracks baked at a fixed frame rate, and where the character stands in the
 * clip's own space. Data only, like a pair clip (`pair-clips.ts`): `npm run animation:import -- --solo` writes one per clip from the glTF Blender
 * exports (see `docs/animation-authoring.md`), and the animator plays it over the figure's own pose. The tracks keep the pair format, so one player
 * blends both (`authored-pair.ts`).
 */
export interface AuthoredSoloClip {
  id: string;
  fps: number;
  /** Number of frames; the clip lasts `(frames - 1) / fps` seconds. A looping clip's last frame matches its first. */
  frames: number;
  seconds: number;
  /** The clip repeats: between frames its curve runs on through the seam instead of stopping at either end. */
  loop?: true;
  /** As a pair role's tracks, except that a track holding still for the whole clip keeps a single frame. */
  tracks: AuthoredRoleTracks;
  /** A per-body version of the shared clip with this id: it plays in that clip's place on bodies of its own sex (`tracks.body`). */
  overrides?: string;
}
const clips = new Map<string, AuthoredSoloClip>(), versions = new Map<string, AuthoredSoloClip>();
const sexOf = (body: string) => body.endsWith('female') ? 'female' : 'male';
export function registerAuthoredSoloClip(clip: AuthoredSoloClip) {
  clips.set(clip.id, clip);
  if (clip.overrides) versions.set(`${clip.overrides}:${sexOf(clip.tracks.body)}`, clip);
}
/** The clip `id`; for a `body` (a rig id), its per-body version when it has one. */
export function authoredSoloClip(id: string, body?: string): AuthoredSoloClip | undefined { return (body && versions.get(`${id}:${sexOf(body)}`)) || clips.get(id); }
export function authoredSoloClips(): AuthoredSoloClip[] { return [...clips.values()]; }
